import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { RecordPanels } from "@/components/shared/RecordPanels";
import { QuickLog } from "@/components/crm/QuickLog";
import DocumentsPanel from "@/components/documents/DocumentsPanel";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Handshake, Plus, MoreVertical, Building2, Trash2, Users, Star, Settings2, GripVertical, Zap, Workflow, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function Opportunities() {  const { has } = useAtlas();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [orgPeople, setOrgPeople] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [activePipelineId, setActivePipelineId] = useState(null);
  const canEdit = has("crm.opportunities.edit");
  const canManagePipeline = has("crm.pipelines.manage");

  const pipeline = pipelines.find((p) => p.id === activePipelineId) || pipelines[0];
  const stages = pipeline?.stages || [];
  const pipeOpps = opps.filter((o) => !pipeline || o.pipeline_id === pipeline.id);

  const load = () => api.get("/crm/opportunities").then((r) => setOpps(r.data)).catch(() => {});
  const loadPipelines = () => api.get("/crm/pipelines").then((r) => { setPipelines(r.data); setActivePipelineId((cur) => (cur && r.data.some((p) => p.id === cur)) ? cur : (r.data[0]?.id || null)); }).catch(() => {});
  const newPipeline = async () => {
    const nm = window.prompt("Name the new pipeline (e.g. 'Partnerships', 'Renewals')");
    if (!nm || !nm.trim()) return;
    try { const { data } = await api.post("/crm/pipelines", { name: nm.trim(), stages: ["New", "Qualified", "Proposal", "Won", "Lost"] }); toast.success(`Pipeline "${nm.trim()}" created`); await loadPipelines(); setActivePipelineId(data.id); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    load();
    loadPipelines();
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected?.organization_id && has("people.view")) {
      api.get(`/people?organization_id=${selected.organization_id}`).then((r) => setOrgPeople(r.data)).catch(() => setOrgPeople([]));
    } else setOrgPeople([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const create = async (v) => {
    await api.post("/crm/opportunities", { ...v, amount: Number(v.amount) || 0, pipeline_id: pipeline.id, stage_id: v.stage_id || stages[0].id });
    toast.success("Opportunity created");
    load();
  };

  const move = async (opp, stageId) => {
    const target = stages.find((s) => s.id === stageId);
    const loss_reason = target?.kind === "lost" ? window.prompt("Loss reason (optional)") : null;
    try {
      const { data } = await api.put(`/crm/opportunities/${opp.id}/stage`, { stage_id: stageId, loss_reason });
      load();
      if (data.status === "won") {
        toast.success(`${opp.name} closed won`);
        if (data.convert_prompt && window.confirm(`${opp.org_name} is now Won. Convert them to a client workspace now?`)) navigate(`/crm/organizations/${data.organization_id}`);
      }
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const total = (sid) => pipeOpps.filter((o) => o.stage_id === sid).reduce((s, o) => s + (o.amount || 0), 0);

  const remove = async (opp) => {
    if (!window.confirm(`Delete opportunity "${opp.name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/crm/opportunities/${opp.id}`);
      toast.success(`Deleted "${opp.name}"`);
      setSelected(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePartner = async (oid) => {
    const cur = selected.partner_org_ids || [];
    const next = cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid];
    try { const { data } = await api.put(`/crm/opportunities/${selected.id}`, { ...selected, partner_org_ids: next }); setSelected(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleContact = async (pid) => {
    const cur = selected.person_ids || [];
    const attaching = !cur.includes(pid);
    const next = attaching ? [...cur, pid] : cur.filter((x) => x !== pid);
    let primary = selected.primary_person_id || null;
    if (attaching && !primary) primary = pid;
    if (!attaching && primary === pid) primary = next[0] || null;
    try { const { data } = await api.put(`/crm/opportunities/${selected.id}`, { ...selected, person_ids: next, primary_person_id: primary }); setSelected(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setPrimary = async (pid) => {
    const primary = selected.primary_person_id === pid ? null : pid;
    try { const { data } = await api.put(`/crm/opportunities/${selected.id}`, { ...selected, primary_person_id: primary }); setSelected(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Opportunities" subtitle={pipeline ? pipeline.name : "Sales pipeline"}>
        {pipelines.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" data-testid="pipeline-switcher"><Workflow className="w-4 h-4 mr-1" /> {pipeline?.name || "Pipeline"} <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {pipelines.map((p) => <DropdownMenuItem key={p.id} onClick={() => setActivePipelineId(p.id)} data-testid={`pipeline-option-${p.id}`}>{p.name}{p.id === pipeline?.id ? "  ✓" : ""}</DropdownMenuItem>)}
              {canManagePipeline && <><DropdownMenuSeparator /><DropdownMenuItem onClick={newPipeline} data-testid="pipeline-new"><Plus className="w-4 h-4 mr-2" /> New pipeline</DropdownMenuItem></>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canManagePipeline && pipeline && <Button variant="outline" onClick={() => setStagesOpen(true)} data-testid="opp-edit-stages-button"><Settings2 className="w-4 h-4 mr-1" /> Edit stages</Button>}
        {canEdit && orgs.length > 0 && stages.length > 0 && <Button onClick={() => setOpen(true)} data-testid="opp-create-button"><Plus className="w-4 h-4 mr-1" /> New Opportunity</Button>}
      </PageHeader>

      {stages.length === 0 ? (
        <EmptyState icon={Handshake} title="No pipeline configured" description="A default Sales Pipeline is seeded for Total Operations." />
      ) : pipeOpps.length === 0 ? (
        <EmptyState icon={Handshake} title="No opportunities yet" description="Create an opportunity for one of your organizations to start tracking deals." actionLabel={canEdit ? "New Opportunity" : undefined} onAction={() => setOpen(true)} />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="crm-opportunities-board">
          {stages.map((st) => {
            const cards = pipeOpps.filter((o) => o.stage_id === st.id);
            const onDrop = (e) => {
              e.preventDefault(); setDragOver(null);
              const id = e.dataTransfer.getData("text/plain");
              const opp = opps.find((x) => x.id === id);
              if (opp && opp.stage_id !== st.id) move(opp, st.id);
            };
            return (
              <div key={st.id} data-testid={`opp-stage-${st.id}`}
                onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOver(st.id); } : undefined}
                onDragLeave={canEdit ? () => setDragOver(null) : undefined}
                onDrop={canEdit ? onDrop : undefined}
                className={cn("flex-shrink-0 w-72 rounded-2xl p-1.5 transition-colors", dragOver === st.id ? "bg-sky-50 ring-2 ring-sky-200" : "")}>
                <div className="flex items-center justify-between pb-2 mb-3 border-b-2 border-slate-200 px-1.5">
                  <span className="font-medium text-sm text-slate-700">{st.name}</span>
                  <span className="text-xs text-slate-400 font-mono tabular">{cards.length} · ${total(st.id).toLocaleString()}</span>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {cards.map((o) => (
                    <div key={o.id} onClick={() => setSelected(o)} data-testid={`opp-card-${o.id}`}
                      draggable={canEdit} onDragStart={(e) => e.dataTransfer.setData("text/plain", o.id)}
                      className="bg-white border border-slate-200 rounded-lg p-3.5 shadow-sm hover:shadow-md transition-shadow cursor-pointer active:cursor-grabbing">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-sm text-slate-900">{o.name}</span>
                        {canEdit && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><button data-testid={`opp-move-${o.id}`}><MoreVertical className="w-4 h-4 text-slate-400" /></button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {stages.filter((s) => s.id !== o.stage_id).map((s) => (
                                <DropdownMenuItem key={s.id} onClick={(e) => { e.stopPropagation(); move(o, s.id); }}>Move to {s.name}</DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={(e) => { e.stopPropagation(); remove(o); }} data-testid={`opp-delete-${o.id}`}><Trash2 className="w-4 h-4 mr-2" /> Delete opportunity</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5"><Building2 className="w-3 h-3" />{o.org_name}</div>
                      {"amount" in o && <div className="font-mono tabular text-sm font-semibold text-slate-800 mt-2">${(o.amount || 0).toLocaleString()}</div>}
                      {o.next_step && <div className="text-[11px] mt-1.5 text-slate-600 truncate" data-testid={`opp-next-${o.id}`}>→ {o.next_step}{o.next_step_date && <span className={o.next_step_date < new Date().toISOString().slice(0, 10) ? " text-red-600 font-semibold" : " text-slate-400"}> · {o.next_step_date}</span>}</div>}
                      <div className="text-[10px] text-slate-400 mt-1">{o.last_activity_at ? `last ${o.last_activity_kind || "touch"} ${new Date(o.last_activity_at).toLocaleDateString()}` : "no activity logged"}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FormModal open={open} onOpenChange={setOpen} title="New Opportunity" testid="opp-form-modal" onSubmit={create}
        fields={[
          { name: "name", label: "Opportunity name", required: true },
          { name: "organization_id", label: "Organization", type: "select", required: true, options: orgs.map((o) => ({ value: o.id, label: o.name })) },
          { name: "amount", label: "Amount ($)", type: "number", default: 0 },
          { name: "stage_id", label: "Stage", type: "select", default: stages[0]?.id, options: stages.map((s) => ({ value: s.id, label: s.name })) },
          { name: "description", label: "Description", type: "textarea" },
        ]}
      />

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{selected.name}</SheetTitle></SheetHeader>
              <div className="mt-2 space-y-1 text-sm text-slate-600">
                <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />{selected.org_name}</div>
                {"amount" in selected && <div className="font-mono tabular text-lg font-semibold text-slate-900">${(selected.amount || 0).toLocaleString()}</div>}
                {selected.description && <p className="text-slate-600 pt-2">{selected.description}</p>}
              </div>
              <div className="pt-3">
                <p className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Handshake className="w-3.5 h-3.5" /> Partner organizations</p>
                <div className="flex flex-wrap gap-1.5">
                  {orgs.filter((o) => o.id !== selected.organization_id).map((o) => {
                    const on = (selected.partner_org_ids || []).includes(o.id);
                    return <button key={o.id} disabled={!canEdit} onClick={() => togglePartner(o.id)} data-testid={`opp-partner-${o.id}`} className={cn("px-2.5 py-1 rounded-full text-xs border transition-colors", on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}>{o.name}</button>;
                  })}
                  {orgs.filter((o) => o.id !== selected.organization_id).length === 0 && <span className="text-xs text-slate-400">Add more organizations to attach partners.</span>}
                </div>
              </div>
              <div className="pt-3">
                <p className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Contacts</p>
                <div className="flex flex-wrap gap-1.5">
                  {orgPeople.map((pp) => {
                    const on = (selected.person_ids || []).includes(pp.id);
                    const isPrimary = selected.primary_person_id === pp.id;
                    return (
                      <div key={pp.id} className="inline-flex items-center">
                        <button disabled={!canEdit} onClick={() => toggleContact(pp.id)} data-testid={`opp-contact-${pp.id}`} className={cn("px-2.5 py-1 text-xs border transition-colors", on ? "bg-blue-600 text-white border-blue-600 rounded-l-full" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 rounded-full")}>{pp.first_name} {pp.last_name}{pp.title ? ` · ${pp.title}` : ""}</button>
                        {on && <button disabled={!canEdit} onClick={() => setPrimary(pp.id)} title={isPrimary ? "Primary contact — click to unset" : "Make primary contact"} data-testid={`opp-primary-${pp.id}`} className={cn("px-1.5 py-1 rounded-r-full text-xs border border-l-0 transition-colors", isPrimary ? "bg-amber-400 text-amber-950 border-amber-400" : "bg-white text-slate-400 border-slate-200 hover:text-amber-500")}><Star className={cn("w-3 h-3", isPrimary && "fill-current")} /></button>}
                      </div>
                    );
                  })}
                  {orgPeople.length === 0 && <span className="text-xs text-slate-400">No contacts at {selected.org_name} yet — add people on the organization.</span>}
                </div>
                {selected.primary_person_id && (() => { const pr = orgPeople.find((x) => x.id === selected.primary_person_id); return pr ? <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Primary: <b className="text-slate-700">{pr.first_name} {pr.last_name}</b> — used for default follow-ups & emails</p> : null; })()}
              </div>
              <QuickLog opp={selected} onDone={(u) => { setSelected(u); load(); }} />
              {canEdit && <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-50" onClick={() => remove(selected)} data-testid="opp-delete-sheet"><Trash2 className="w-4 h-4 mr-1.5" /> Delete opportunity</Button>}
              <div className="mt-5">
                <RecordPanels relatedType="opportunity" relatedId={selected.id} relatedLabel={selected.name} sourceApp="crm" />
              </div>
              <div className="mt-5">
                <DocumentsPanel opportunityId={selected.id} organizationId={selected.organization_id} orgName={selected.org_name} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <PipelineStagesDialog pipeline={pipeline} open={stagesOpen} onOpenChange={setStagesOpen} onChanged={() => { loadPipelines(); load(); }} />
    </div>
  );
}

function PipelineStagesDialog({ pipeline, open, onOpenChange, onChanged }) {
  const [name, setName] = useState("");
  const [stages, setStages] = useState([]);
  const [newStage, setNewStage] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [autoFor, setAutoFor] = useState(null);
  useEffect(() => {
    if (pipeline) { setName(pipeline.name || ""); setStages((pipeline.stages || []).map((s) => ({ ...s }))); }
  }, [pipeline, open]);
  if (!pipeline) return null;

  const saveName = async () => {
    const n = name.trim();
    if (!n || n === pipeline.name) return;
    try { await api.put(`/crm/pipelines/${pipeline.id}`, { name: n }); toast.success("Pipeline renamed"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const patchStage = async (sid, patch) => {
    try { await api.patch(`/crm/pipelines/${pipeline.id}/stages/${sid}`, patch); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addStage = async () => {
    const n = newStage.trim(); if (!n) return;
    setBusy(true);
    try { await api.post(`/crm/pipelines/${pipeline.id}/stages`, { name: n }); setNewStage(""); toast.success("Stage added"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const removeStage = async (s) => {
    if (!window.confirm(`Delete stage "${s.name}"? Opportunities in it must be moved first.`)) return;
    try { await api.delete(`/crm/pipelines/${pipeline.id}/stages/${s.id}`); toast.success("Stage removed"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const persistOrder = async (list) => {
    try { await api.post(`/crm/pipelines/${pipeline.id}/stages/reorder`, { stage_ids: list.map((s) => s.id) }); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const drop = (idx) => {
    if (drag === null || drag === idx) { setDrag(null); setOver(null); return; }
    const next = [...stages];
    const [moved] = next.splice(drag, 1);
    next.splice(idx, 0, moved);
    setStages(next); setDrag(null); setOver(null);
    persistOrder(next);
  };
  const keyMove = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= stages.length) return;
    const next = [...stages];
    [next[idx], next[j]] = [next[j], next[idx]];
    setStages(next); persistOrder(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pipeline-stages-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit pipeline stages</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label className="text-xs font-medium text-slate-500">Pipeline name</label>
            <div className="flex gap-2 mt-1">
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pipeline-name-input" />
              <Button variant="outline" onClick={saveName} data-testid="pipeline-name-save">Rename</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Stages — drag to reorder, rename, set outcome, automate, add or remove</label>
            {stages.map((s, i) => (
              <div key={s.id}>
                <div
                  draggable
                  onDragStart={() => setDrag(i)}
                  onDragOver={(e) => { e.preventDefault(); setOver(i); }}
                  onDrop={() => drop(i)}
                  onDragEnd={() => { setDrag(null); setOver(null); }}
                  className={cn("flex items-center gap-2 rounded-lg border p-2 transition-colors", over === i && drag !== null && drag !== i ? "ring-2 ring-sky-300 bg-sky-50/50" : "", drag === i ? "opacity-40" : "")}
                  style={{ borderColor: "var(--line)" }}
                  data-testid={`pipeline-stage-row-${s.id}`}>
                  <button
                    className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500"
                    title="Drag to reorder (or use arrow keys)"
                    onKeyDown={(e) => { if (e.key === "ArrowUp") { e.preventDefault(); keyMove(i, -1); } if (e.key === "ArrowDown") { e.preventDefault(); keyMove(i, 1); } }}
                    data-testid={`stage-drag-${s.id}`}><GripVertical className="w-4 h-4" /></button>
                  <Input defaultValue={s.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.name) patchStage(s.id, { name: v }); }} className="flex-1 h-8" data-testid={`stage-name-${s.id}`} />
                  <select value={s.kind || "open"} onChange={(e) => { setStages(stages.map((x) => x.id === s.id ? { ...x, kind: e.target.value } : x)); patchStage(s.id, { kind: e.target.value }); }} className="h-8 rounded-md border text-xs px-1.5 bg-white" style={{ borderColor: "var(--line)" }} data-testid={`stage-kind-${s.id}`}>
                    <option value="open">Open</option><option value="won">Won</option><option value="lost">Lost</option>
                  </select>
                  <button onClick={() => setAutoFor(autoFor === s.id ? null : s.id)} className={cn("hover:text-amber-500", autoFor === s.id ? "text-amber-500" : "text-slate-300")} title="Automations" data-testid={`stage-automations-toggle-${s.id}`}><Zap className="w-4 h-4" /></button>
                  <button onClick={() => removeStage(s)} className="text-slate-300 hover:text-red-500" data-testid={`stage-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                {autoFor === s.id && <StageAutomations pipelineId={pipeline.id} stage={s} />}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newStage} onChange={(e) => setNewStage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStage()} placeholder="New stage name" data-testid="pipeline-new-stage-input" />
            <Button onClick={addStage} disabled={busy} data-testid="pipeline-add-stage"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StageAutomations({ pipelineId, stage }) {
  const [autos, setAutos] = useState([]);
  const [type, setType] = useState("task");
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);
  const base = `/crm/pipelines/${pipelineId}/stages/${stage.id}/automations`;
  const loadA = () => api.get(base).then((r) => setAutos(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadA(); }, [stage.id]);
  const add = async () => {
    setBusy(true);
    try { await api.post(base, { type, config: cfg }); setCfg({}); toast.success("Automation added"); loadA(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`${base}/${id}`); loadA(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="ml-6 mt-1 mb-2 rounded-lg bg-slate-50 border p-2.5 space-y-2" style={{ borderColor: "var(--line)" }} data-testid={`stage-automations-${stage.id}`}>
      <p className="text-[11px] font-medium text-slate-500">When a deal enters <b>{stage.name}</b>:</p>
      {autos.map((a) => (
        <div key={a.id} className="flex items-center justify-between text-xs bg-white rounded border px-2 py-1" style={{ borderColor: "var(--line)" }} data-testid={`automation-${a.id}`}>
          <span className="text-slate-600 truncate">{a.type === "task" ? `Create task: "${a.config?.title || "Follow up: {deal}"}"${a.config?.assignee === "owner" || !a.config?.assignee ? " for the deal owner" : ""}` : `Email ${a.config?.to === "primary_contact" ? "the primary contact" : "the deal owner"}: "${a.config?.subject || "{deal} — {stage}"}"`}</span>
          <button onClick={() => del(a.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0 ml-2" data-testid={`automation-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {autos.length === 0 && <p className="text-[11px] text-slate-400">No automations yet.</p>}
      <div className="flex flex-wrap items-center gap-1.5">
        <select value={type} onChange={(e) => { setType(e.target.value); setCfg({}); }} className="h-7 rounded border text-xs px-1.5 bg-white" style={{ borderColor: "var(--line)" }} data-testid={`automation-type-${stage.id}`}>
          <option value="task">Create task</option><option value="email">Send email</option>
        </select>
        {type === "task" ? (
          <input value={cfg.title || ""} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} placeholder="Task title (use {deal}, {org})" className="h-7 rounded border text-xs px-2 flex-1 min-w-[150px]" style={{ borderColor: "var(--line)" }} data-testid={`automation-task-title-${stage.id}`} />
        ) : (
          <>
            <select value={cfg.to || "owner"} onChange={(e) => setCfg({ ...cfg, to: e.target.value })} className="h-7 rounded border text-xs px-1.5 bg-white" style={{ borderColor: "var(--line)" }} data-testid={`automation-email-to-${stage.id}`}><option value="owner">Deal owner</option><option value="primary_contact">Primary contact</option></select>
            <input value={cfg.subject || ""} onChange={(e) => setCfg({ ...cfg, subject: e.target.value })} placeholder="Subject (use {deal}, {stage})" className="h-7 rounded border text-xs px-2 flex-1 min-w-[130px]" style={{ borderColor: "var(--line)" }} data-testid={`automation-email-subject-${stage.id}`} />
          </>
        )}
        <Button size="sm" className="h-7 text-xs" onClick={add} disabled={busy} data-testid={`automation-add-${stage.id}`}>Add</Button>
      </div>
    </div>
  );
}
